import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBlogById, getPublishedBlogs } from '../utils/blogStorage';
import { FixedNav } from '../components';
import { arrow, mail, linkedin } from '../assets';

export const IndividualBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [moreStories, setMoreStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchedBlog = getBlogById(id);
    setBlog(fetchedBlog);
    setMoreStories(getPublishedBlogs().filter((b) => b.id !== id).slice(0, 3));
    setLoading(false);
    window.scrollTo({ top: 0 });
  }, [id]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Could not copy link:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-custom-background flex items-center justify-center">
        <p className="text-charcoal text-lg">Loading...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-custom-background flex items-center justify-center px-10">
        <div className="text-center">
          <h1 className="font-grandslang text-[60px] text-charcoal mb-6">
            Blog Not Found
          </h1>
          <p className="text-charcoal mb-6">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/insights')}
            className="px-8 py-4 border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-custom-background transition-all duration-300 text-sm tracking-wider uppercase"
          >
            View All Stories
          </button>
        </div>
      </div>
    );
  }

  const [heroImage, ...restImages] = blog.images || [];

  return (
    <div className="min-h-screen bg-custom-background">
      <FixedNav />

      <article className="pt-28 pb-24 px-5 sm:px-10 lg:px-20">
        <div className="max-w-3xl mx-auto animate-fade-in-up">
          <button
            onClick={() => navigate('/insights')}
            className="group mb-12 flex items-center gap-3 text-xs uppercase tracking-[3px] font-semibold text-charcoal/50 hover:text-[#ff6347] transition-colors duration-300"
          >
            <img
              src={arrow}
              alt=""
              className="h-3 rotate-180 transition-transform duration-300 group-hover:-translate-x-1"
            />
            All Stories
          </button>

          <p className="text-xs uppercase tracking-[4px] font-semibold text-[#ff6347] mb-4">
            Story
          </p>

          <h1 className="font-grandslang text-4xl sm:text-5xl md:text-6xl text-charcoal leading-[1.05] mb-6">
            {blog.title}
          </h1>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-10 text-xs uppercase tracking-widest text-charcoal/40 font-mono">
            <span>{formatDate(blog.createdAt)}</span>
            {blog.categories && blog.categories.length > 0 && (
              <>
                <span className="opacity-50">&bull;</span>
                {blog.categories.map((cat, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 border border-charcoal/20 rounded-full tracking-[0.2em] text-charcoal/60"
                  >
                    {cat}
                  </span>
                ))}
              </>
            )}
          </div>

          {heroImage && (
            <figure className="mb-10 -mx-5 sm:mx-0">
              <div className="aspect-[16/9] overflow-hidden bg-charcoal/5">
                <img
                  src={heroImage.url}
                  alt={heroImage.caption || blog.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {heroImage.caption && (
                <figcaption className="mt-3 text-xs text-charcoal/50 italic text-center">
                  {heroImage.caption}
                </figcaption>
              )}
            </figure>
          )}

          {blog.excerpt && (
            <p className="mb-12 text-xl md:text-2xl text-charcoal/70 font-light leading-relaxed border-l-2 border-[#ff6347] pl-6">
              {blog.excerpt}
            </p>
          )}

          <div
            className="blog-content text-charcoal mb-16"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          {restImages.length > 0 && (
            <div className="mb-16 grid sm:grid-cols-2 gap-6">
              {restImages.map((image, index) => (
                <figure key={index} className="w-full">
                  <img
                    src={image.url}
                    alt={image.caption || `Image ${index + 2}`}
                    className="w-full h-auto object-cover"
                  />
                  {image.caption && (
                    <figcaption className="mt-2 text-xs text-charcoal/50 italic text-center">
                      {image.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          )}

          {blog.tags && blog.tags.length > 0 && (
            <div className="mb-16 pt-8 border-t border-charcoal/10">
              <p className="text-xs text-charcoal/40 mb-4 uppercase tracking-[3px] font-semibold">Tags</p>
              <div className="flex gap-3 flex-wrap">
                {blog.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 border border-charcoal/20 rounded-full text-charcoal/60 text-xs uppercase tracking-widest hover:border-charcoal hover:text-charcoal transition-all cursor-default"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-6 pt-8 border-t border-charcoal/10">
            <div className="flex items-center gap-5">
              <p className="text-xs uppercase tracking-[3px] font-semibold text-charcoal/40">Share</p>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                aria-label="Share on LinkedIn"
              >
                <img src={linkedin} alt="" className="w-5 h-5 opacity-60 hover:opacity-100 transition-opacity" />
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(blog.title)}&body=${encodeURIComponent(shareUrl)}`}
                aria-label="Share by email"
              >
                <img src={mail} alt="" className="w-5 h-5 opacity-60 hover:opacity-100 transition-opacity" />
              </a>
              <button
                onClick={handleCopyLink}
                className="text-xs uppercase tracking-widest text-charcoal/60 hover:text-[#ff6347] transition-colors"
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>

            <button
              onClick={() => navigate('/insights')}
              className="px-8 py-4 border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-custom-background transition-all duration-300 text-xs font-semibold tracking-[3px] uppercase"
            >
              View All Insights
            </button>
          </div>
        </div>
      </article>

      {moreStories.length > 0 && (
        <section className="bg-charcoal/[0.03] py-24 px-5 sm:px-10 lg:px-20">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs uppercase tracking-[4px] font-semibold text-[#ff6347] mb-10">
              More Stories
            </p>
            <div className="flex flex-col">
              {moreStories.map((story) => (
                <button
                  key={story.id}
                  onClick={() => navigate(`/insights/${story.id}`)}
                  className="group flex items-center justify-between gap-6 py-8 border-b border-charcoal/10 text-left hover:pl-2 transition-all duration-300"
                >
                  <div>
                    <h3 className="font-grandslang text-2xl md:text-3xl text-charcoal group-hover:text-[#ff6347] transition-colors duration-300">
                      {story.title}
                    </h3>
                    <p className="text-xs uppercase tracking-widest text-charcoal/40 font-mono mt-2">
                      {formatDate(story.createdAt)}
                    </p>
                  </div>
                  <img
                    src={arrow}
                    alt=""
                    className="h-4 shrink-0 opacity-40 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
